import { ringLayout } from '../orbit-math';

/**
 * Where the globe sits on screen relative to the podium. Shared by the 3D
 * scene (camera altitude/offset) and by the static poster shown while the
 * scene loads, so both line up exactly. Kept outside the lazy three.js chunk.
 */

export const CAMERA_FOV = 50;
/** Globe radius on screen relative to the podium half-width. */
const GLOBE_TO_RING = 0.54;
/** How far the globe sinks into the podium, relative to the podium half-width. */
const GLOBE_SINK = 0.27;

export interface StageSize {
    width: number;
    height: number;
    platformWidth: number;
    platformBottom: number;
}

export interface GlobeCircle {
    cx: number;
    cy: number;
    r: number;
}

export function globeCircle(size: StageSize, ringRadius: number, flatten: number): GlobeCircle | null {
    const { width, height, platformWidth, platformBottom } = size;

    if (width <= 0 || height <= 0 || platformWidth <= 0) {
        return null;
    }

    const ring = ringLayout(platformWidth, ringRadius, flatten);

    return {
        cx: width / 2,
        cy: platformBottom - ring.deckPlaneFromBottom - ring.rx * GLOBE_SINK,
        r: ring.rx * (width < 720 ? GLOBE_TO_RING * 1.3 : GLOBE_TO_RING),
    };
}

export function cameraFor(size: StageSize, ringRadius: number, flatten: number): { altitude: number; offset: [number, number] } {
    const circle = globeCircle(size, ringRadius, flatten);

    if (!circle) {
        return { altitude: 2.15, offset: [0, 0] };
    }

    const halfFov = (CAMERA_FOV / 2) * (Math.PI / 180);
    const angle = Math.atan((circle.r * Math.tan(halfFov)) / (size.height / 2));
    const sin = Math.sin(angle);
    const altitude = sin > 0 ? Math.min(12, Math.max(1.4, 1 / sin - 1)) : 2.15;

    return { altitude, offset: [0, Math.round(circle.cy - size.height / 2)] };
}
